import { useEffect, useRef } from 'react';
import { useCerbos } from '@cerbos/react';
import { ANY_ID } from '@skynet/authz-core';
import { UINavigationCommands } from './types';
import type { NavigationPermissionsAdapter } from './NavigationPermissionsAdapter';

export interface NavigationAccessCheckResult {
  data: string[] | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Checks navigation access via Cerbos and stores the result via the provided adapter.
 * The check runs once on mount (when permissions are not yet loaded).
 *
 * @param adapter - State adapter connecting this hook to the app's store
 * @param actions - The navigation action names to check permissions for
 */
export const useNavigationAccessCheck = (
  adapter: NavigationPermissionsAdapter,
  actions: string[],
): NavigationAccessCheckResult => {
  const client = useCerbos();
  // Keep a ref to the adapter so the effect always reads the latest callbacks
  // without needing adapter in the dependency array (adapter object is recreated each render).
  const adapterRef = useRef(adapter);
  adapterRef.current = adapter;

  useEffect(() => {
    const { permissions, isLoading } = adapterRef.current;
    if (permissions !== null || isLoading) return;

    adapterRef.current.setLoading();

    let cancelled = false;
    void (async () => {
      try {
        const resource = await client.checkResource({
          resource: { kind: UINavigationCommands.UINavigation, id: ANY_ID },
          actions,
        });
        if (cancelled) {
          return;
        }
        if (adapterRef.current.permissions === null) {
          adapterRef.current.setPermissions(resource.allowedActions());
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        if (adapterRef.current.permissions === null) {
          adapterRef.current.setError(error);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [client, actions]);

  return {
    data: adapter.permissions,
    isLoading: adapter.isLoading || (adapter.permissions === null && !adapter.isError),
    isError: adapter.isError,
  };
};
